import React from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  borderGrey,
  extraSmallTextSize,
  mediumText,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  whiteBG,
} from '../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../services/staff/staffService';
import { useStaffStatusActions } from '../hooks/useStaffStatusActions';
import { getHeaderAccessType } from '../staffAccessUtils';
import StaffAccessBadges from './StaffAccessBadges';
import StaffAvatar from './StaffAvatar';

interface Restaurant {
  id: string;
  name: string;
}

interface StaffUserCardProps {
  user: FrontendStaffUser;
  restaurants: Restaurant[];
  isSelected?: boolean;
  /** Whether the viewer may manage THIS member — gates the approve/reject quick
   *  actions. Defaults closed. */
  canManage?: boolean;
  onSelect: () => void;
}

const StaffUserCard: React.FC<StaffUserCardProps> = ({
  user,
  restaurants,
  isSelected = false,
  canManage = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { approve, reject } = useStaffStatusActions(user.id);

  const getRestaurantName = (id: string) => restaurants.find((r) => r.id === id)?.name || id;
  const inScopeRestaurantIds = user.restaurant_ids.filter((id) => restaurants.some((r) => r.id === id));
  const sortedNames = inScopeRestaurantIds
    .map((id) => ({ id, name: getRestaurantName(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const displayNames = sortedNames.slice(0, 2);
  const remainingCount = user.restaurant_ids.length - displayNames.length;
  const displayName = user.name?.trim() || user.email;
  const roleLabel = user.restaurant_role || '—';
  const headerAccessType = getHeaderAccessType(user);

  const status = user.access_request;
  // Rejected members keep an Approve action so a mistaken rejection is reversible.
  const showQuickActions = canManage && (status === 'pending' || status === 'rejected');

  const statusBadgeClass = cn(extraSmallTextSize, 'px-2 rounded-md leading-6 shrink-0');
  const quickActionClass = cn(smallTextSize, semiBoldText, 'px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors');

  return (
    <div
      className={cn(
        'rounded-lg border p-3 transition-colors shadow-[0px_1px_1.5px_0.1px_#16192408] dark:shadow-[0px_1px_1.5px_0.1px_#00000040]',
        whiteBG,
        isSelected
          ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10'
          : cn(borderGrey, 'hover:border-darkGreenColor/40 dark:hover:border-darkGreenColor-dark/50')
      )}
    >
      <button type="button" onClick={onSelect} className="w-full text-left flex items-start gap-3">
        <StaffAvatar user={user} size="md" />

        <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className={cn(semiBoldText, primaryText, 'text-[18px] leading-6 truncate')}>{displayName}</h2>
              {status === 'pending' && (
                <span className={cn(statusBadgeClass, 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300')}>
                  {t('staff.accessRequest.pending')}
                </span>
              )}
              {status === 'rejected' && (
                <span className={cn(statusBadgeClass, 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300')}>
                  {t('staff.accessRequest.rejected')}
                </span>
              )}
            </div>

            <p className={cn(smallTextSize, secondaryText, 'leading-6 font-normal mt-1 truncate')}>{roleLabel}</p>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {displayNames.map((r) => (
                <span
                  key={r.id}
                  className={cn(
                    'px-[9px] py-[3px] rounded-lg border bg-transparent dark:bg-transparent leading-5',
                    extraSmallTextSize,
                    mediumText,
                    borderGrey,
                    tertiaryText
                  )}
                >
                  {r.name}
                </span>
              ))}
              {remainingCount > 0 && (
                <span className={cn(extraSmallTextSize, mediumText, tertiaryText, 'leading-5')}>
                  {t('staff.moreRestaurantsShort', { count: remainingCount })}
                </span>
              )}
              <StaffAccessBadges accessType={headerAccessType} />
            </div>
          </div>

          <svg
            width="24"
            height="24"
            className={cn('shrink-0', tertiaryText)}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      {showQuickActions && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            type="button"
            onClick={approve}
            className={cn(
              quickActionClass,
              'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
              'hover:bg-emerald-200 dark:hover:bg-emerald-900/50'
            )}
          >
            <Check className="w-4 h-4" />
            {t('staff.actions.approve')}
          </button>
          {status === 'pending' && (
            <button
              type="button"
              onClick={reject}
              className={cn(
                quickActionClass,
                'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
                'hover:bg-red-200 dark:hover:bg-red-900/50'
              )}
            >
              <X className="w-4 h-4" />
              {t('staff.actions.reject')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffUserCard;
